import i18n from '@/i18n';
import { workflowTaskAPI } from '@/utils/api';
import { normalizeTemplate } from './ipeWorkflowStorage';
import {
  assertVideoToolsAvailable,
  workflowPipelineUsesVideoTools,
} from '@/pages/ipe/workbench/constants/videoToolsMaintenance';
import { resolveTemplatePipelineSteps, locateStepInStages, resolveTemplateStepStages } from '@/pages/ipe/workbench/utils/workflowStepPlan';
import { runPipelineForRows } from '@/pages/ipe/workbench/utils/sourcingWorkflowPipeline';
import { persistSourcingWorkflowRuns } from '@/pages/ipe/workbench/utils/sourcingWorkflowRunHistory';
import { finalizeWorkflowListingExport } from '@/pages/ipe/workbench/utils/workflowListingExport';
import {
  waitForInfringementDecision,
  waitForGoodsStampAssignment,
  buildInitialRuntimeStepOverrides,
  applyRuntimeStepOverridesToRows,
  cancelInfringementWaiters,
  cancelGoodsStampWaiters,
  cancelPreAssignWaiters,
} from '@/pages/ipe/workbench/utils/workflowInteractive';
import {
  resolveWorkflowTaskId,
  resolveWorkflowTaskIdFromList,
} from './workflowTaskId';
import {
  buildWorkflowRowStepDisplay,
  buildPreservedWorkflowStepDisplay,
  classifyWorkflowRowOutcome,
  findFirstRetryWorkflowStepIndex,
  isWorkflowStepExecutable,
  pickWorkflowStepLinkFields,
} from './workflowNavId';
import { generateWorkflowTaskBatchNo } from '@/pages/ipe/workbench/utils/workflowTaskBatchNo';
import { applyWorkflowWorkspaceFields } from '@/pages/ipe/workbench/utils/workflowWorkspaceMeta';
import { hydrateModelStudioRuntimeOverrides } from '@/pages/ipe/workbench/utils/resolveModelStudioPresetForReuse';
import { getIpeUserLocalOwnerId, userLocal } from '@/utils/ipeUserLocalState';
import { isWorkflowServerNav } from '@/pages/ipe/workbench/constants/workflowStepCatalog';

export { resolveWorkflowTaskId } from './workflowTaskId';

/** 人工审核通过后应继续执行的首个 flat 步骤下标 */
function resolveWorkflowReviewContinueStepIndex(template, reviewStepIndex) {
  const stages = resolveTemplateStepStages(template);
  const safeReview = Math.max(0, Number(reviewStepIndex) || 0);
  const loc = locateStepInStages(stages, safeReview);
  if (!loc) return safeReview + 1;
  if (loc.isParallel) {
    return loc.stageStartIndex + loc.stage.length;
  }
  return safeReview + 1;
}

export const WORKFLOW_TASKS_STORAGE_KEY = 'ipe-workflow-tasks-v2';
const WORKFLOW_TASKS_DELETED_IDS_KEY = 'ipe-workflow-tasks-deleted-ids-v1';

/** 同时执行的工作流任务数上限（与服务端 Redis 队列一致） */
export const WORKFLOW_MAX_CONCURRENT = 2;

/** 浏览器 runner 心跳间隔；需明显短于服务端 ipe.workflow.client-lease-seconds */
const CLIENT_PRESENCE_HEARTBEAT_MS = 15000;


const EVENT_NAME = 'ipe-workflow-tasks-updated';

let memoryTasks = null;
let hydratePromise = null;
/** 首次服务端 hydrate 是否已落地；未落地时勿把本地 running 快照展示成「处理中」 */
let hydrateSettled = false;
/** force 刷新时递增，丢弃更早的列表响应，避免慢请求覆盖新数据 */
let hydrateSeq = 0;
/** 用户点击终止后标记，executeWorkflowTask / runPipelineForRows 轮询此标记 */
const taskAbortFlags = new Set();
let runnerStarted = false;
const runningTaskIds = new Set();
/** 等待服务端 create 完成后再 acquireSlot / patch，避免 404 导致进度不同步 */
const serverCreatePromises = new Map();
let queuePumpTimer = null;
let clientPresenceTimer = null;
let clientPresenceBound = false;

const WORKFLOW_TASK_SYNC_OMIT_KEYS = new Set([
  'slideCanvases',
  'fabricCanvas',
  'previewDataUrl',
  'preview_data_url',
  'canvasJson',
]);

/** 去掉画布 JSON 和内嵌 base64，列表同步和本地兜底缓存才能及时落地 */
export function slimWorkflowTaskPayload(task) {
  if (!task || typeof task !== 'object') return task;
  try {
    const json = JSON.stringify(task, (key, value) => {
      if (WORKFLOW_TASK_SYNC_OMIT_KEYS.has(key)) return undefined;
      if (typeof value === 'string' && value.startsWith('data:image') && value.length > 256) {
        return undefined;
      }
      return value;
    });
    return JSON.parse(json);
  } catch {
    return task;
  }
}

function taskUpdatedAtMs(task) {
  const n = Date.parse(task?.updatedAt || '');
  return Number.isFinite(n) ? n : 0;
}

function workflowResultEntryScore(entry, expectedRunId) {
  if (!entry?.result) return 0;
  const r = entry.result;
  const steps = r.steps;
  let detail = 0;
  if (Array.isArray(steps)) {
    for (const s of steps) {
      if (s?.inputImageUrl || s?.outputImageUrl) detail += 2;
      else if (s?.status) detail += 1;
    }
  }
  // 正在重跑的条目占位，必须压过上一轮已完成的 ok/侵权 结果
  if (r.inProgress) return 2_000_000 + detail;
  // 用户「终止当前条目」后的 skipped 结果必须覆盖旧的 ok 全绿记录（否则 UI 仍显示 √）
  if (r.skipped) return 1_000_000 + detail;
  if (!r.ok && r.error) return 500_000 + detail;
  let base = 0;
  if (r.ok) base = 10_000 + detail;
  else base = detail;
  if (expectedRunId != null) {
    if (r.workflowRunId === expectedRunId) {
      base += 5_000_000;
    } else if (r.workflowRunId != null && r.workflowRunId !== expectedRunId) {
      return -1;
    }
  }
  return base;
}

/** 按行下标合并多条结果数组，保留步骤/图片更完整的那条；可选 { workflowRunId } 作为最后一参 */
export function mergeWorkflowResultArrays(...sources) {
  let expectedRunId = null;
  const arrays = [];
  for (const src of sources) {
    if (Array.isArray(src)) {
      arrays.push(src);
      continue;
    }
    if (src && typeof src === 'object' && src.workflowRunId != null) {
      expectedRunId = src.workflowRunId;
    }
  }
  const merged = [];
  for (const src of arrays) {
    src.forEach((entry, i) => {
      if (!entry?.result && !entry?.row) return;
      const prev = merged[i];
      if (
        !prev ||
        workflowResultEntryScore(entry, expectedRunId) > workflowResultEntryScore(prev, expectedRunId)
      ) {
        merged[i] = entry;
      }
    });
  }
  return merged;
}

export function getWorkflowTaskResultEntries(task) {
  if (!task) return [];
  return mergeWorkflowResultArrays(task.results, task.summary?.results);
}

const COMPLETED_STEP_DISPLAY_STATUSES = new Set(['done', 'ok', 'skipped', 'risk']);

/** 条目已完成的连续模块数；全部完成时返回步骤总数 */
function inferRowResumeStepIndex(entry, pipelineSteps) {
  if (!Array.isArray(pipelineSteps) || pipelineSteps.length === 0) return 0;
  const display = Array.isArray(entry?.stepDisplay) && entry.stepDisplay.length
    ? entry.stepDisplay
    : buildWorkflowRowStepDisplay(pipelineSteps, entry?.result, () => ({ status: 'pending' }));
  for (let i = 0; i < pipelineSteps.length; i += 1) {
    const st = String(display[i]?.status || '').toLowerCase();
    if (COMPLETED_STEP_DISPLAY_STATUSES.has(st)) continue;
    return i;
  }
  return pipelineSteps.length;
}

/**
 * 刷新后续跑：从已完成模块继续，已全部完成则落终态，禁止从头重跑。
 */
function inferWorkflowResumeCursor(task) {
  const template = normalizeTemplate(task?.templateSnapshot);
  const pipelineSteps = template ? resolveTemplatePipelineSteps(template) : [];
  const entries = getWorkflowTaskResultEntries(task);
  const rowTotal = task?.rows?.length || entries.length || 0;
  const stored = String(task?.status || '');
  if (isTerminalWorkflowStatus(stored) && stored !== 'terminated') {
    const derived = deriveWorkflowTaskTerminalStatus(task);
    return {
      terminalStatus: derived || stored,
      resumeFromRowIndex: rowTotal,
      resumeStepIndex: pipelineSteps.length,
    };
  }
  if (stored === 'terminated') {
    return { terminalStatus: 'terminated', resumeFromRowIndex: rowTotal, resumeStepIndex: pipelineSteps.length };
  }
  if (pipelineSteps.length === 0 || rowTotal === 0) {
    return { terminalStatus: null, resumeFromRowIndex: 0, resumeStepIndex: 0 };
  }

  let minCompleted = pipelineSteps.length;
  let anyProgress = false;
  let allRowsFullyDone = true;
  for (let i = 0; i < rowTotal; i += 1) {
    const completed = inferRowResumeStepIndex(entries[i], pipelineSteps);
    if (completed > 0) anyProgress = true;
    minCompleted = Math.min(minCompleted, completed);
    if (completed < pipelineSteps.length) allRowsFullyDone = false;
  }
  if (!anyProgress) minCompleted = 0;
  if (allRowsFullyDone) {
    return {
      terminalStatus: deriveWorkflowTaskTerminalStatus(task) || 'done',
      resumeFromRowIndex: rowTotal,
      resumeStepIndex: pipelineSteps.length,
    };
  }
  return {
    terminalStatus: null,
    resumeFromRowIndex: 0,
    resumeStepIndex: Math.max(minCompleted, Number(task?.resumeStepIndex) || 0),
  };
}

function mergeWorkflowResultsAtEnd(
  priorResults,
  runtimeResults,
  pipelineResults,
  startRowIndex,
  workflowRunId,
  rowIndices = null,
) {
  const merged = mergeWorkflowResultArrays(priorResults, runtimeResults, { workflowRunId });
  if (!Array.isArray(pipelineResults)) return merged;
  pipelineResults.forEach((entry, off) => {
    const i = Array.isArray(rowIndices) && rowIndices.length ? rowIndices[off] : startRowIndex + off;
    const tagged =
      entry?.result && workflowRunId != null
        ? { ...entry, result: { ...entry.result, workflowRunId } }
        : entry;
    const prev = merged[i];
    if (
      !prev ||
      workflowResultEntryScore(tagged, workflowRunId) > workflowResultEntryScore(prev, workflowRunId)
    ) {
      merged[i] = tagged;
    }
  });
  return merged;
}

export function countWorkflowTaskOutcome(task) {
  const total = task?.rows?.length || Number(task?.summary?.total) || 0;
  const entries = getWorkflowTaskResultEntries(task);
  const template = normalizeTemplate(task?.templateSnapshot);
  const pipelineSteps = template ? resolveTemplatePipelineSteps(template) : [];
  let successCount = 0;
  let failedCount = 0;
  let partialCount = 0;
  entries.forEach((entry) => {
    const result = entry?.result;
    const outcome = classifyWorkflowRowOutcome(result, pipelineSteps);
    if (outcome === 'pending' || outcome === 'review') return;
    if (outcome === 'success') successCount += 1;
    else if (outcome === 'partial') partialCount += 1;
    else failedCount += 1;
  });
  const summaryOk = Number(task?.summary?.okCount);
  if (Number.isFinite(summaryOk) && summaryOk > successCount) {
    successCount = summaryOk;
  }
  return { total, successCount, failedCount, partialCount };
}

/** 根据条数成败推导终态；无法判断时返回 null */
function deriveWorkflowTaskTerminalStatus(task) {
  const { total, successCount, failedCount, partialCount } = countWorkflowTaskOutcome(task);
  if (total <= 0) return null;
  const processedCount = successCount + failedCount + partialCount;
  if (processedCount <= 0) return null;
  if (successCount >= total) return 'done';
  if (partialCount > 0) return 'partial';
  if (successCount > 0 && failedCount > 0) return 'partial';
  if (successCount === 0 && failedCount > 0) return 'failed';
  return null;
}

/** 展示/筛选用：按条目实际成败纠正任务终态 */
export function resolveWorkflowTaskStatus(task) {
  if (!task) return '';
  let stored = String(task.status || '');
  if (stored === 'terminated') return 'terminated';
  if (stored === 'cancelled') stored = 'failed';
  if (stored === 'running' || stored === 'queued' || stored === 'pendingReview') {
    return stored;
  }
  const derived = deriveWorkflowTaskTerminalStatus(task);
  if (derived) return derived;
  if (stored === 'partial') {
    const { partialCount, failedCount } = countWorkflowTaskOutcome(task);
    if (partialCount > 0) return 'partial';
    if (failedCount > 0) return 'failed';
  }
  return stored;
}

function isWorkflowSnapshotHydrating() {
  return !hydrateSettled;
}

/**
 * 刷新后本地仍可能残留 running，但本页 runner 尚未接手。
 * 这段窗口展示「正在加载」，避免看起来像重新跑一遍。
 */
export function resolveWorkflowTaskDisplayStatus(task) {
  const resolved = resolveWorkflowTaskStatus(task);
  if (
    (resolved === 'running' || resolved === 'queued')
    && !isTaskInRunner(task)
    && isWorkflowSnapshotHydrating()
  ) {
    return 'loading';
  }
  return resolved;
}

/** 已完成/已终止任务：合并本地与服务端快照，避免 hydrate 用不完整的旧结果覆盖本地 */
function mergeTerminalWorkflowSnapshot(server, local) {
  if (!local) return server;
  if (!server) return local;
  const preferLocal = taskUpdatedAtMs(local) >= taskUpdatedAtMs(server);
  const base = preferLocal
    ? { ...server, ...local, id: server.id || local.id }
    : { ...local, ...server, id: server.id || local.id };
  const results = mergeWorkflowResultArrays(
    server.results,
    server.summary?.results,
    local.results,
    local.summary?.results,
  );
  if (results.length > 0) {
    base.results = results;
    const okCount = results.filter((x) => x?.result?.ok).length;
    const total = base.rows?.length || local.summary?.total || server.summary?.total || results.length;
    base.summary = {
      ...(server.summary || {}),
      ...(local.summary || {}),
      results,
      okCount: Math.max(okCount, Number(local.summary?.okCount) || 0, Number(server.summary?.okCount) || 0),
      total,
    };
  }
  const resolvedStatus = resolveWorkflowTaskStatus(base);
  if (resolvedStatus) base.status = resolvedStatus;
  return applyTerminalDisplayFix(base);
}

function isActiveWorkflowTask(task) {
  return task?.status === 'running' || task?.status === 'queued';
}

function isTerminalWorkflowStatus(status) {
  return status === 'done' || status === 'success' || status === 'partial' || status === 'failed' || status === 'terminated';
}

function mapWorkflowStepEventStatus(status) {
  switch (String(status || '')) {
    case 'skipped':
      return 'skipped';
    case 'stopped':
      return 'stopped';
    case 'risk':
      return 'risk';
    case 'failed':
      return 'failed';
    case 'running':
      return 'running';
    case 'done':
    case 'ok':
      return 'done';
    default:
      return 'pending';
  }
}

/** 把 onStep 写进对应印花行的 stepDisplay，表格才能立刻出现「处理中」而不只改任务级 live */
function applyWorkflowStepEventToResults(results, pipelineSteps, ev, emptyFactory) {
  const rowIndex = Number(ev?.rowIndex);
  const idx = Number(ev?.index);
  if (!Number.isFinite(rowIndex) || rowIndex < 0) return results;
  if (!Number.isFinite(idx) || idx < 0) return results;
  const empty = typeof emptyFactory === 'function' ? emptyFactory : (() => ({ status: 'pending' }));
  const stepCount = Array.isArray(pipelineSteps) ? pipelineSteps.length : 0;
  const merged = Array.isArray(results) ? [...results] : [];
  const entry = merged[rowIndex] || {
    row: ev.row,
    result: { inProgress: true, ok: false, steps: [] },
    stepDisplay: Array.from({ length: stepCount }, () => empty()),
  };
  const stepDisplay = Array.isArray(entry.stepDisplay) && entry.stepDisplay.length
    ? entry.stepDisplay.map((st) => ({ ...(st || empty()) }))
    : Array.from({ length: stepCount }, () => empty());
  while (stepDisplay.length < stepCount) stepDisplay.push(empty());
  const mapped = mapWorkflowStepEventStatus(ev.status);
  const prev = stepDisplay[idx] || empty();
  stepDisplay[idx] = {
    ...prev,
    ...pickWorkflowStepLinkFields(ev),
    status: mapped,
    detail: ev.detail || ev.error || prev.detail || '',
    inputImageUrl: ev.inputImageUrl || prev.inputImageUrl || '',
    outputImageUrl: ev.outputImageUrl || prev.outputImageUrl || '',
    outputImageUrlDisplay: ev.outputImageUrlDisplay || prev.outputImageUrlDisplay || '',
    viewUrl: ev.viewUrl || prev.viewUrl || '',
    displayUrl: ev.displayUrl || prev.displayUrl || ev.viewUrl || prev.viewUrl || '',
    compositeImageViewUrls: Array.isArray(ev.compositeImageViewUrls)
      ? ev.compositeImageViewUrls
      : (prev.compositeImageViewUrls || []),
    outputVideoUrl: ev.outputVideoUrl || prev.outputVideoUrl || '',
    outputVideoUrls: Array.isArray(ev.outputVideoUrls) ? ev.outputVideoUrls : (prev.outputVideoUrls || []),
    previewLeftLabel: ev.previewLeftLabel || prev.previewLeftLabel || '',
    previewCenterLabel: ev.previewCenterLabel || prev.previewCenterLabel || '',
    previewRightLabel: ev.previewRightLabel || prev.previewRightLabel || '',
  };
  merged[rowIndex] = {
    ...entry,
    stepDisplay,
    result: {
      ...(entry.result || {}),
      inProgress: true,
    },
  };
  return merged;
}

/** 运行中提示文案（任务已进入终态后若仍是这些提示，说明是残留在快照里的旧值） */
const RUNNING_MESSAGE_PATTERN = /^\s*(正在处理|准备中|等待)/;

function freezeRunningStepDisplay(stepDisplay, terminalStatus) {
  if (!Array.isArray(stepDisplay)) return stepDisplay;
  let changed = false;
  const next = stepDisplay.map((st) => {
    if (!st || (st.status !== 'running' && st.status !== 'pending')) return st;
    // 处理中 → 终态 stopped/failed
    if (st.status === 'running') {
      changed = true;
      return {
        ...st,
        status: terminalStatus === 'terminated' ? 'stopped' : 'failed',
        detail: st.detail || (terminalStatus === 'terminated' ? '任务已终止' : '未完成'),
      };
    }
    // pending 但已有失败明细（onStep 已写 error、status 却被覆盖回 pending）→ 纠成 failed/stopped
    if (st.status === 'pending' && String(st.detail || '').trim()) {
      changed = true;
      return {
        ...st,
        status: terminalStatus === 'terminated' ? 'stopped' : 'failed',
      };
    }
    return st;
  });
  return changed ? next : stepDisplay;
}

function freezeWorkflowResultEntry(entry, terminalStatus) {
  if (!entry?.result) return entry;
  let changed = false;
  let result = entry.result;
  if (result.inProgress) {
    changed = true;
    result = {
      ...result,
      inProgress: false,
      // 未产出终态时保持 ok=false，交由 classifyWorkflowRowOutcome 判 failed
      ok: result.ok === true ? true : false,
    };
  }
  const stepDisplay = freezeRunningStepDisplay(entry.stepDisplay, terminalStatus);
  if (stepDisplay !== entry.stepDisplay) changed = true;
  return changed ? { ...entry, result, stepDisplay } : entry;
}

/**
 * 终态展示修正：任务已结束，但快照里仍残留「处理中 / 正在处理第 N 条」时改写成终态文案。
 * 避免「批次已终止，下方仍显示正在处理第 X 条、其他条目仍是处理中」。
 */
function applyTerminalDisplayFix(task) {
  if (!task) return task;
  const stored = String(task.status || '');
  if (!isTerminalWorkflowStatus(stored)) return task;

  let changed = false;
  let results = task.results;
  if (Array.isArray(results)) {
    const next = results.map((entry) => freezeWorkflowResultEntry(entry, stored));
    if (next.some((entry, i) => entry !== results[i])) {
      changed = true;
      results = next;
    }
  }

  let summary = task.summary;
  if (summary && Array.isArray(summary.results)) {
    const nextSummaryResults = summary.results.map((entry) => freezeWorkflowResultEntry(entry, stored));
    if (nextSummaryResults.some((entry, i) => entry !== summary.results[i])) {
      changed = true;
      summary = { ...summary, results: nextSummaryResults };
    }
  }

  let live = task.live;
  if (live && typeof live === 'object') {
    const msg = String(live.lastMessage || '');
    const stepStatuses = freezeRunningStepDisplay(live.stepStatuses, stored);
    const needMsgFix = !msg || RUNNING_MESSAGE_PATTERN.test(msg);
    if (needMsgFix || stepStatuses !== live.stepStatuses) {
      changed = true;
      live = {
        ...live,
        stepStatuses: stepStatuses || live.stepStatuses,
        lastMessage: needMsgFix
          ? (stored === 'terminated'
            ? '任务已终止'
            : stored === 'failed'
              ? '任务执行失败'
              : stored === 'partial'
                ? '任务已结束（部分条目失败）'
                : '任务已完成')
          : live.lastMessage,
      };
    }
  }

  return changed ? { ...task, results, summary, live } : task;
}

function isServerOwnedWorkflowTask(task) {
  return task?.executor === 'server' || task?.serverRunner === true;
}

function isWorkflowServerAdvanceable(template, progress) {
  const steps = resolveTemplatePipelineSteps(template) || [];
  const idx = Math.max(0, Number(progress?.stepIndex) || 0);
  return isWorkflowServerNav(steps[idx]?.navId);
}

function loadDeletedWorkflowTaskIds() {
  const merged = new Set();
  try {
    const parsed = userLocal.getJson(WORKFLOW_TASKS_DELETED_IDS_KEY, []);
    if (Array.isArray(parsed)) {
      parsed.forEach((id) => merged.add(String(id)));
    }
  } catch {
    /* ignore */
  }
  try {
    const raw = sessionStorage.getItem(WORKFLOW_TASKS_DELETED_IDS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) {
      parsed.forEach((id) => merged.add(String(id)));
    }
  } catch {
    /* ignore */
  }
  return merged;
}

function saveDeletedWorkflowTaskIds(ids) {
  const arr = [...ids];
  try {
    userLocal.setJson(WORKFLOW_TASKS_DELETED_IDS_KEY, arr);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(WORKFLOW_TASKS_DELETED_IDS_KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

function markWorkflowTaskDeleted(taskId) {
  if (!taskId) return;
  const ids = loadDeletedWorkflowTaskIds();
  ids.add(String(taskId));
  saveDeletedWorkflowTaskIds(ids);
}

function markWorkflowTaskDeletedKeys(task) {
  if (!task) return;
  markWorkflowTaskDeleted(task.id);
  if (task.legacyLocalId) markWorkflowTaskDeleted(task.legacyLocalId);
  if (task.createdAt) markWorkflowTaskDeleted(`created:${task.createdAt}`);
}

function isTaskRecordDeleted(task) {
  if (!task) return false;
  const deletedIds = loadDeletedWorkflowTaskIds();
  if (deletedIds.has(String(task.id))) return true;
  if (task.legacyLocalId && deletedIds.has(String(task.legacyLocalId))) return true;
  if (task.createdAt && deletedIds.has(`created:${task.createdAt}`)) return true;
  return false;
}

function filterDeletedWorkflowTasks(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((task) => !isTaskRecordDeleted(task) && task?.status !== 'draft' && task?.status !== 'cancelled');
}

function migrateWorkflowTaskId(fromId, toId, serverFields = {}) {
  if (!fromId || !toId || fromId === toId) return toId;
  const list = loadWorkflowTasks();
  const idx = list.findIndex((t) => t.id === fromId);
  if (idx < 0) return toId;
  list[idx] = {
    ...list[idx],
    ...serverFields,
    id: toId,
    legacyLocalId: fromId,
    updatedAt: new Date().toISOString(),
  };
  if (runningTaskIds.has(fromId)) {
    runningTaskIds.delete(fromId);
    runningTaskIds.add(toId);
  }
  saveWorkflowTasks(list);
  return toId;
}

/** 人工审核通过后：从 pendingReview 还原续跑位置（优先于 onRowDone 误写的 resumeFromRowIndex） */
function resolveWorkflowReviewResumeIndices(task) {
  const pending = task?.pendingReview;
  const template = normalizeTemplate(task?.templateSnapshot);
  const rowIndex = Math.max(
    0,
    Number.isFinite(Number(pending?.rowIndex))
      ? Number(pending.rowIndex)
      : Number(task?.resumeFromRowIndex) || 0,
  );
  const reviewStep = Number.isFinite(Number(pending?.stepIndex))
    ? Number(pending.stepIndex)
    : (Number.isFinite(Number(task?.resumeStepIndex)) && Number(task?.resumeStepIndex) > 0
      ? Number(task.resumeStepIndex) - 1
      : -1);
  const stepIndex = reviewStep >= 0
    ? resolveWorkflowReviewContinueStepIndex(template, reviewStep)
    : Math.max(0, Number(task?.resumeStepIndex) || 0);
  return { rowIndex, stepIndex: Math.max(0, stepIndex) };
}

function resolveWorkflowRowResumeStepIndex({
  rowIndex,
  startRowIndex,
  resumeStepIndex,
  retryRowIndices,
  retryStepByRow,
  priorResults,
  template,
}) {
  if (retryRowIndices?.length) {
    const mapped = retryStepByRow[rowIndex] ?? retryStepByRow[String(rowIndex)];
    return Number.isFinite(Number(mapped)) ? Number(mapped) : 0;
  }
  const pipelineSteps = template ? resolveTemplatePipelineSteps(template) : [];
  const fromResults = inferRowResumeStepIndex(priorResults[rowIndex], pipelineSteps);
  if (rowIndex === startRowIndex) {
    return Math.max(fromResults, Number(resumeStepIndex) || 0);
  }
  const prior = priorResults[rowIndex]?.result;
  if (prior?.pendingReview && Number.isFinite(Number(prior.reviewAtStepIndex))) {
    return resolveWorkflowReviewContinueStepIndex(template, Number(prior.reviewAtStepIndex));
  }
  if (fromResults > 0) return fromResults;
  return Math.max(0, Number(resumeStepIndex) || 0);
}

function findWorkflowTaskIndex(list, taskId) {
  if (!taskId || !Array.isArray(list)) return -1;
  const resolvedId = resolveWorkflowTaskId(taskId);
  let idx = list.findIndex((t) => t.id === resolvedId);
  if (idx >= 0) return idx;
  return list.findIndex((t) => t.id === taskId || t.legacyLocalId === taskId);
}

function isTaskAbortRequested(taskOrId) {
  if (!taskOrId) return false;
  if (typeof taskOrId === 'string') {
    return taskAbortFlags.has(taskOrId);
  }
  const t = taskOrId;
  if (taskAbortFlags.has(t.id)) return true;
  if (t.legacyLocalId && taskAbortFlags.has(t.legacyLocalId)) return true;
  return false;
}

function isTaskInRunner(task) {
  if (!task) return false;
  if (runningTaskIds.has(task.id)) return true;
  if (task.legacyLocalId && runningTaskIds.has(task.legacyLocalId)) return true;
  return false;
}

/** 仅本页正在执行的任务以本地 progress / live 为准，避免服务端 hydrate 把 UI 进度打回 0 */
function applyRunningLocalPreference(local, merged) {
  if (!isTaskInRunner(local)) return merged;
  if (local?.status !== 'running') return merged;
  if (isTaskAbortRequested(local)) {
    return { ...merged, status: 'terminated' };
  }
  const out = { ...merged, status: 'running' };
  if (local.progress && typeof local.progress === 'object') {
    out.progress = local.progress;
  }
  if (local.live && typeof local.live === 'object') {
    out.live = local.live;
  }
  if (Array.isArray(local.results)) out.results = local.results;
  if (local.resumeFromRowIndex != null) out.resumeFromRowIndex = local.resumeFromRowIndex;
  if (local.runtimeStepOverrides) out.runtimeStepOverrides = local.runtimeStepOverrides;
  return out;
}

function loadLocalTasksForMerge() {
  return filterDeletedWorkflowTasks(
    memoryTasks != null ? memoryTasks : loadFromLocalStorage(),
  );
}

function mergeWorkflowTasksFromServer(serverTasks) {
  const localRaw = loadLocalTasksForMerge();
  if (!Array.isArray(serverTasks) || serverTasks.length === 0) {
    return localRaw;
  }

  const visibleServerTasks = serverTasks.filter((st) => !isTaskRecordDeleted(st));

  const merged = visibleServerTasks.map((st) => {
    const lt = localRaw.find((x) => x.id === st.id || x.legacyLocalId === st.id);
    if (!lt) return st;
    if (isTerminalWorkflowStatus(lt.status) && isActiveWorkflowTask(st)) {
      // 本地终态比服务端更新时才保留，避免旧的「已完成」快照盖住服务端正在跑的新进度
      if (taskUpdatedAtMs(lt) > taskUpdatedAtMs(st)) {
        return { ...st, ...lt, id: st.id };
      }
      return applyTerminalDisplayFix({
        ...lt,
        ...st,
        id: st.id,
        templateSnapshot: st.templateSnapshot || lt.templateSnapshot,
      });
    }
    if (isTaskAbortRequested(lt) || isTaskAbortRequested(st.id)) {
      return { ...st, ...lt, id: st.id, status: 'terminated', terminatedByUser: true };
    }
    if (lt.status === 'queued' && st.status === 'running' && !isTaskInRunner(lt)) {
      if (isServerOwnedWorkflowTask(st)) {
        return { ...lt, ...st, id: st.id, status: 'running' };
      }
      return { ...st, ...lt, id: st.id, status: 'queued' };
    }
    if ((lt.status === 'running' || lt.status === 'queued') && !isTaskInRunner(lt)) {
      if (isTerminalWorkflowStatus(st.status)) {
        return mergeTerminalWorkflowSnapshot(st, lt);
      }
    }
    if (isTaskInRunner(lt) || isTaskInRunner(st.id)) {
      return applyRunningLocalPreference(lt, { ...st, ...lt, id: st.id, status: 'running' });
    }
    if (!isActiveWorkflowTask(lt)) {
      return mergeTerminalWorkflowSnapshot(st, lt);
    }
    if (!isTaskInRunner(lt)) {
      // 本页没在跑：服务端是最新操作的来源，不能让本地旧快照靠 updatedAt 盖住它
      return applyTerminalDisplayFix({
        ...lt,
        ...st,
        id: st.id,
        templateSnapshot: st.templateSnapshot || lt.templateSnapshot,
      });
    }
    let merged;
    if (taskUpdatedAtMs(lt) >= taskUpdatedAtMs(st)) {
      merged = { ...st, ...lt, id: st.id };
    } else {
      merged = { ...lt, ...st, id: st.id };
    }
    return applyRunningLocalPreference(lt, merged);
  });

  const mergedIds = new Set(merged.map((t) => t.id));
  localRaw.forEach((lt) => {
    if (mergedIds.has(lt.id)) return;
    if (isTaskRecordDeleted(lt)) return;
    const onServer = visibleServerTasks.some((st) => st.id === lt.id || st.id === lt.legacyLocalId);
    if (!onServer) merged.push(lt);
  });

  merged.sort((a, b) => taskUpdatedAtMs(b) - taskUpdatedAtMs(a));
  return filterDeletedWorkflowTasks(merged).map(applyTerminalDisplayFix);
}

function newTaskId() {
  return `wftask-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emitUpdated() {
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {
    /* ignore */
  }
}

function loadFromLocalStorage() {
  try {
    const parsed = userLocal.getJson(WORKFLOW_TASKS_STORAGE_KEY, []);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveWorkflowTasks(list, { fromHydrate = false } = {}) {
  // 未绑定用户时禁止回写内存，避免 logout 后异步任务把上一账号列表重新灌进 memoryTasks
  if (!getIpeUserLocalOwnerId()) {
    memoryTasks = null;
    return;
  }
  let next = list;
  if (fromHydrate && memoryTasks != null && runningTaskIds.size > 0) {
    const preserved = new Map();
    memoryTasks.forEach((t) => {
      if (isTaskInRunner(t)) preserved.set(t.id, t);
    });
    if (preserved.size > 0) {
      next = list.map((t) => {
        const keep = preserved.get(t.id)
          || (t.legacyLocalId ? preserved.get(t.legacyLocalId) : null);
        if (!keep) return t;
        return applyRunningLocalPreference(keep, { ...t, ...keep, id: t.id, status: 'running' });
      });
    }
  }
  memoryTasks = next;
  const saved = userLocal.setJson(WORKFLOW_TASKS_STORAGE_KEY, next);
  if (!saved) {
    userLocal.setJson(WORKFLOW_TASKS_STORAGE_KEY, next.map((task) => slimWorkflowTaskPayload(task)));
  }
  emitUpdated();
}

export function clearWorkflowTasksForLogout() {
  stopWorkflowClientPresence();
  memoryTasks = null;
  hydratePromise = null;
  hydrateSettled = false;
  taskAbortFlags.clear();
  runningTaskIds.clear();
  try {
    serverCreatePromises.clear();
  } catch {
    /* ignore */
  }
  userLocal.dropLegacyKeys(WORKFLOW_TASKS_STORAGE_KEY, WORKFLOW_TASKS_DELETED_IDS_KEY);
  try {
    sessionStorage.removeItem(WORKFLOW_TASKS_DELETED_IDS_KEY);
  } catch {
    /* ignore */
  }
  emitUpdated();
}

/** 从服务端拉取任务列表（MySQL 主存 + Redis 排队） */
export function hydrateWorkflowTasksFromServer({ force = false } = {}) {
  if (!force && hydrateSettled && runningTaskIds.size > 0) {
    return hydratePromise || Promise.resolve();
  }
  // 进行中的拉取一律复用：force 也不叠发（列表单次可达数 MB，叠发会打成死循环流量）
  if (hydratePromise) {
    return hydratePromise;
  }
  const seq = ++hydrateSeq;
  const promise = workflowTaskAPI
    .list()
    .then((res) => {
      if (seq !== hydrateSeq) return;
      const tasks = res?.tasks ?? res?.data;
      if (Array.isArray(tasks)) {
        const merged = tasks.length > 0
          ? mergeWorkflowTasksFromServer(tasks)
          : filterDeletedWorkflowTasks(loadLocalTasksForMerge());
        saveWorkflowTasks(merged, { fromHydrate: true });
      } else if (memoryTasks == null) {
        memoryTasks = filterDeletedWorkflowTasks(loadFromLocalStorage());
      }
    })
    .catch(() => {
      if (seq !== hydrateSeq) return;
      if (memoryTasks == null) {
        memoryTasks = filterDeletedWorkflowTasks(loadFromLocalStorage());
      }
    })
    .finally(() => {
      if (hydratePromise === promise) hydratePromise = null;
      if (seq !== hydrateSeq) return;
      hydrateSettled = true;
      const saved = reconcileOrphanedWorkflowTasks();
      if (runnerStarted) {
        pumpWorkflowRunner();
      }
      // hydrateSettled 翻转后必须换新引用，否则 React 会跳过同一 task 对象的 setState
      if (!saved && Array.isArray(memoryTasks)) {
        memoryTasks = memoryTasks.map((t) => ({ ...t }));
      }
      emitUpdated();
    });
  hydratePromise = promise;
  return promise;
}

export function loadWorkflowTasks() {
  if (memoryTasks == null) {
    memoryTasks = filterDeletedWorkflowTasks(loadFromLocalStorage());
    hydrateWorkflowTasksFromServer();
  }
  return filterDeletedWorkflowTasks(memoryTasks);
}

export function getWorkflowTaskById(taskId) {
  if (!taskId) return null;
  const list = loadWorkflowTasks();
  const resolvedId = resolveWorkflowTaskId(taskId);
  return (
    list.find((t) => t.id === resolvedId || t.id === taskId || t.legacyLocalId === taskId) || null
  );
}

export function isWorkflowTaskLive(task) {
  return isTaskInRunner(task);
}

function mergeDetailWorkflowTask(server, local) {
  if (!local) return applyTerminalDisplayFix(server);
  if (isTaskAbortRequested(local) || isTaskAbortRequested(server?.id)) {
    return { ...server, ...local, id: server.id, status: 'terminated', terminatedByUser: true };
  }
  if (isTerminalWorkflowStatus(local.status) && isActiveWorkflowTask(server)) {
    return { ...server, ...local, id: server.id };
  }
  if (!isActiveWorkflowTask(server) || !isActiveWorkflowTask(local)) {
    return mergeTerminalWorkflowSnapshot(server, local);
  }
  const preferLocal = taskUpdatedAtMs(local) >= taskUpdatedAtMs(server);
  const merged = preferLocal
    ? { ...server, ...local, id: server.id }
    : { ...local, ...server, id: server.id };
  return applyTerminalDisplayFix(merged);
}

/** 详情页专用：拉服务端完整任务后再展示，避免先用本地快照画出「虚拟」结果 */
export function hydrateWorkflowTaskDetail(taskId) {
  const resolvedId = resolveWorkflowTaskId(taskId);
  if (!resolvedId) return Promise.resolve(null);
  const local = getWorkflowTaskById(resolvedId);
  if (isTaskInRunner(local)) return Promise.resolve(local);
  return fetchRemoteWorkflowTask(resolvedId).then((remote) => {
    const latestLocal = getWorkflowTaskById(resolvedId);
    if (isTaskInRunner(latestLocal)) return latestLocal;
    if (!remote) return latestLocal;
    const merged = mergeDetailWorkflowTask(remote, latestLocal);
    const list = loadWorkflowTasks();
    const idx = findWorkflowTaskIndex(list, resolvedId);
    if (idx >= 0) {
      const next = list.slice();
      next[idx] = merged;
      saveWorkflowTasks(next);
    } else {
      saveWorkflowTasks([merged, ...list]);
    }
    return merged;
  });
}

export function subscribeWorkflowTasks(listener) {
  if (typeof window === 'undefined') return () => {};
  ensureWorkflowRunnerStarted();
  const handler = () => listener(loadWorkflowTasks());
  window.addEventListener(EVENT_NAME, handler);
  listener(loadWorkflowTasks());
  return () => window.removeEventListener(EVENT_NAME, handler);
}

function patchTaskToServer(taskId, payload, attempt = 0) {
  const maxAttempts = 3;
  const body = slimWorkflowTaskPayload(payload);
  workflowTaskAPI.patch(taskId, body).catch(() => {
    if (attempt >= maxAttempts) return;
    window.setTimeout(
      () => patchTaskToServer(taskId, payload, attempt + 1),
      1200 * (attempt + 1),
    );
  });
}

function patchTask(taskId, patch) {
  const list = loadWorkflowTasks();
  const idx = findWorkflowTaskIndex(list, taskId);
  if (idx < 0) return null;
  const prev = list[idx];
  const resolvedId = resolveWorkflowTaskId(taskId);
  const next = { ...prev, ...patch, updatedAt: new Date().toISOString() };
  if (patch?.progress && prev.progress && typeof patch.progress === 'object') {
    next.progress = { ...prev.progress, ...patch.progress };
  }
  if (patch?.live && prev.live && typeof patch.live === 'object') {
    next.live = { ...prev.live, ...patch.live };
  }
  const aborted = isTaskAbortRequested(resolvedId) || isTaskAbortRequested(taskId);
  if (aborted && (prev.status === 'running' || prev.status === 'terminated') && patch.status !== 'queued') {
    next.status = 'terminated';
    if (patch.resumeFromRowIndex != null && prev.resumeFromRowIndex != null) {
      next.resumeFromRowIndex = prev.resumeFromRowIndex;
    }
  }
  const finalTask = applyTerminalDisplayFix(next);
  list[idx] = finalTask;
  saveWorkflowTasks(list);
  patchTaskToServer(resolvedId, finalTask);
  return finalTask;
}

/** 前端已建 funding 任务后写入，关页由 Worker 轮询该任务，避免重跑再扣费。 */
export function setWorkflowAwaitingFundingTask(workflowTaskId, fundingTaskId) {
  const wfId = String(workflowTaskId || '').trim();
  if (!wfId) return null;
  const id = String(fundingTaskId || '').trim();
  return patchTask(wfId, {
    progress: { awaitingFundingTaskId: id || null },
  });
}

/** 合并 onStep 高频更新，减轻 localStorage 同步与 UI 重渲染对主线程的压力 */
const WORKFLOW_LIVE_PATCH_MS = 450;

function createDebouncedTaskPatch(taskId, delayMs = WORKFLOW_LIVE_PATCH_MS) {
  let timer = null;
  let queued = null;

  const mergeQueued = (patch) => {
    if (!queued) return patch;
    return {
      ...queued,
      ...patch,
      progress: patch.progress
        ? { ...(queued.progress || {}), ...patch.progress }
        : queued.progress,
      live: patch.live ? { ...(queued.live || {}), ...patch.live } : queued.live,
    };
  };

  return {
    now: (patch) => patchTask(taskId, patch),
    debounced(patch) {
      // 任务已进入终态后不再接受「处理中」类 live 补丁，避免覆盖终止文案
      const cur = getWorkflowTaskById(taskId);
      if (cur && isTerminalWorkflowStatus(cur.status)) return;
      queued = mergeQueued(patch);
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        const p = queued;
        queued = null;
        if (!p) return;
        const latest = getWorkflowTaskById(taskId);
        if (latest && isTerminalWorkflowStatus(latest.status)) return;
        patchTask(taskId, p);
      }, delayMs);
    },
    flush() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (queued) {
        const p = queued;
        queued = null;
        const latest = getWorkflowTaskById(taskId);
        if (latest && isTerminalWorkflowStatus(latest.status)) return;
        patchTask(taskId, p);
      }
    },
    /** 丢弃未落地的 live 补丁（终态收尾时用，避免 flush 把「正在处理」盖回来） */
    discard() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      queued = null;
    },
  };
}

/**
 * @param {{
 *   template: object,
 *   rows: Array<{ id, title?, primaryImageUrl?, stepOverrides? }>,
 *   remark?: string,
 *   remarkFlag?: string,
 *   groups?: Array<{ id: string, name: string, rowIds: string[], stepOverrides?: object }>,
 *   workspaceContext?: { user?: object, scope?: object, authUserId?: string|number|null },
 * }} payload
 */
export function createWorkflowTask(payload) {
  const template = normalizeTemplate(payload.template);
  if (!template) throw new Error(i18n.t('workbench:ipeUtilError.workflowTemplateInvalid'));

  const rows = (payload.rows || []).map((r) => ({
    id: r.id,
    title: r.title || '',
    primaryImageUrl: r.primaryImageUrl || '',
    stepOverrides: r.stepOverrides && typeof r.stepOverrides === 'object' ? r.stepOverrides : {},
  }));
  if (rows.length === 0) throw new Error(i18n.t('workbench:ipeUtilError.workflowNeedMaterial'));

  // 前置校验：模板必须含可执行步骤，避免建出「空跑」任务
  const pipelineSteps = resolveTemplatePipelineSteps(template);
  if (pipelineSteps.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.workflowNoExecutableSteps'));
  }
  const videoMaintenance = assertVideoToolsAvailable();
  if (!videoMaintenance.ok && workflowPipelineUsesVideoTools(pipelineSteps)) {
    throw new Error(videoMaintenance.message);
  }
  const unsupportedStep = pipelineSteps.find((s) => !isWorkflowStepExecutable(s));
  if (unsupportedStep) {
    const label = unsupportedStep.label || unsupportedStep.navId || i18n.t('workbench:ipeUtilError.unknownStep');
    throw new Error(i18n.t('workbench:ipeUtilError.workflowStepNotAutoExecutable', { label }));
  }

  const createdAt = new Date().toISOString();
  const workspaceCtx = payload.workspaceContext;
  let task = {
    id: newTaskId(),
    batchNo: generateWorkflowTaskBatchNo(new Date(createdAt)),
    status: 'queued',
    createdAt,
    updatedAt: createdAt,
    remark: String(payload.remark || '').trim(),
    remarkFlag: String(payload.remarkFlag || '').trim(),
    templateId: template.id,
    templateName: template.name || '工作流',
    templateSnapshot: template,
    groups: Array.isArray(payload.groups) ? payload.groups : [],
    rows,
    stampPreAssign: payload.stampPreAssign && typeof payload.stampPreAssign === 'object'
      ? payload.stampPreAssign
      : null,
    // 多印花位：详情表按组展示（每组 N 张印花图同一行）
    stampGroupSize: Math.max(
      1,
      Number(payload.stampGroupSize)
        || Number(payload.stampPreAssign?.slotCount)
        || 1,
    ),
    progress: {
      rowIndex: 0,
      rowTotal: rows.length,
      stepIndex: 0,
      stepLabel: '',
    },
    live: {
      rowIndex: 0,
      stepStatuses: [],
      previewOriginal: '',
      previewResult: '',
      lastMessage: '',
    },
    summary: null,
    results: null,
    error: null,
    runtimeStepOverrides: buildInitialRuntimeStepOverrides(template),
    /** 浏览器在线时优先本地 pipeline；关页后由 Worker 凭槽位续跑，不再用 clientExecutor 锁死 */
    preferredExecutor: 'client',
    executor: 'client',
    serverRunner: false,
    clientExecutor: false,
    serverExecute: isWorkflowServerAdvanceable(template),
  };
  if (workspaceCtx?.scope) {
    task = applyWorkflowWorkspaceFields(
      task,
      workspaceCtx.user,
      workspaceCtx.authUserId,
      workspaceCtx.scope,
    );
  }

  const list = loadWorkflowTasks();
  list.unshift(task);
  saveWorkflowTasks(list);
  const createPromise = workflowTaskAPI
    .create(task)
    .then((res) => {
      const serverTask = res?.task;
      if (serverTask?.id && serverTask.id !== task.id) {
        migrateWorkflowTaskId(task.id, serverTask.id, serverTask);
      }
      pumpWorkflowRunner();
      return serverTask?.id || task.id;
    })
    .catch(() => task.id)
    .finally(() => {
      serverCreatePromises.delete(task.id);
      const resolved = resolveWorkflowTaskId(task.id);
      if (resolved !== task.id) serverCreatePromises.delete(resolved);
    });
  serverCreatePromises.set(task.id, createPromise);
  ensureWorkflowRunnerStarted();
  return task;
}

/** 将分组上的 stepOverrides 合并到各行 */
export function applyGroupOverridesToRows(rows, groups) {
  if (!Array.isArray(groups) || groups.length === 0) {
    return rows.map((r) => ({
      ...r,
      stepOverrides: r.stepOverrides && typeof r.stepOverrides === 'object' ? r.stepOverrides : {},
    }));
  }
  const rowOverrideMap = new Map();
  groups.forEach((g) => {
    const ov = g.stepOverrides && typeof g.stepOverrides === 'object' ? g.stepOverrides : {};
    (g.rowIds || []).forEach((rid) => {
      rowOverrideMap.set(String(rid), ov);
    });
  });
  return rows.map((r) => {
    const fromGroup = rowOverrideMap.get(String(r.id)) || {};
    const own = r.stepOverrides && typeof r.stepOverrides === 'object' ? r.stepOverrides : {};
    const merged = { ...fromGroup };
    Object.keys(own).forEach((navId) => {
      merged[navId] = { ...(merged[navId] || {}), ...(own[navId] || {}) };
    });
    return { ...r, stepOverrides: merged };
  });
}

function stopWorkflowClientPresence() {
  if (typeof window === 'undefined') return;
  if (clientPresenceTimer) {
    window.clearInterval(clientPresenceTimer);
    clientPresenceTimer = null;
  }
}

/** 有本地/服务端进行中或排队时才需要续浏览器租约；全终态停心跳，避免空闲刷 client-presence */
function needsWorkflowClientPresence() {
  if (runningTaskIds.size > 0) return true;
  return loadWorkflowTasks().some((t) => {
    const status = resolveWorkflowTaskStatus(t);
    return status === 'running' || status === 'queued';
  });
}

function tickWorkflowClientPresence() {
  if (!needsWorkflowClientPresence()) {
    stopWorkflowClientPresence();
    return;
  }
  workflowTaskAPI.heartbeat().catch(() => {});
  if (runningTaskIds.size > 0) return;
  const hasServerRunning = loadWorkflowTasks().some(
    (t) => isServerOwnedWorkflowTask(t) && resolveWorkflowTaskStatus(t) === 'running',
  );
  // 仅在本页没有本地 runner、但服务端仍有 running 时补拉；有进行中的 hydrate 会复用
  if (hasServerRunning) {
    hydrateWorkflowTasksFromServer({ force: false });
  }
}

function startWorkflowClientPresence() {
  if (typeof window === 'undefined') return;
  if (!clientPresenceBound) {
    clientPresenceBound = true;
    window.addEventListener('pagehide', stopWorkflowClientPresence);
  }
  if (!needsWorkflowClientPresence()) {
    stopWorkflowClientPresence();
    return;
  }
  // 先发一拍心跳，再确保 interval 在跑（从空闲恢复时会重新拉起）
  workflowTaskAPI.heartbeat().catch(() => {});
  if (!clientPresenceTimer) {
    clientPresenceTimer = window.setInterval(tickWorkflowClientPresence, CLIENT_PRESENCE_HEARTBEAT_MS);
  }
}

function scheduleQueuePump(delayMs = 2500) {
  if (typeof window === 'undefined') return;
  if (queuePumpTimer) window.clearTimeout(queuePumpTimer);
  queuePumpTimer = window.setTimeout(() => {
    queuePumpTimer = null;
    pumpWorkflowRunner();
  }, delayMs);
}

/**
 * 已发过「释放槽位」的任务 id。
 * 已完成/终止的任务服务端早已释放槽位，这里只为历史遗留 id 兜底一次；
 * 若每次 pump 都对所有历史任务各发一遍，会把 release-slot 刷爆（且全是 200 success 的无效请求）。
 */
const releasedInactiveSlotIds = new Set();

function releaseSlotsForInactiveTasks() {
  loadWorkflowTasks().forEach((t) => {
    if (isActiveWorkflowTask(t)) return;
    const ids = [t.id, t.legacyLocalId].filter(Boolean);
    if (ids.length === 0) return;
    if (ids.every((id) => releasedInactiveSlotIds.has(id))) return;
    ids.forEach((id) => {
      releasedInactiveSlotIds.add(id);
      workflowTaskAPI.releaseSlot(id).catch(() => {});
    });
  });
}

/** 页面刷新后本地仍标记 running、但本页未在执行的任务：已完成落终态，未完成从已跑模块续跑 */
function reconcileOrphanedWorkflowTasks() {
  releaseSlotsForInactiveTasks();
  const list = loadWorkflowTasks();
  let dirty = false;
  const next = list.map((t) => {
    if (t.status !== 'running') return t;
    if (isServerOwnedWorkflowTask(t)) return t;
    const id = t.id;
    const legacy = t.legacyLocalId;
    if (runningTaskIds.has(id) || (legacy && runningTaskIds.has(legacy))) return t;
    dirty = true;
    const inferred = inferWorkflowResumeCursor(t);
    if (inferred.terminalStatus) {
      workflowTaskAPI.releaseSlot(id).catch(() => {});
      return applyTerminalDisplayFix({
        ...t,
        status: inferred.terminalStatus,
        resumeFromRowIndex: inferred.resumeFromRowIndex,
        resumeStepIndex: inferred.resumeStepIndex,
        live: {
          ...(t.live || {}),
          lastMessage: inferred.terminalStatus === 'terminated' ? '任务已终止' : '任务已完成',
        },
      });
    }
    workflowTaskAPI.releaseSlot(id).catch(() => {});
    return {
      ...t,
      status: 'queued',
      resumeFromRowIndex: inferred.resumeFromRowIndex,
      resumeStepIndex: inferred.resumeStepIndex,
      live: {
        ...(t.live || {}),
        lastMessage: inferred.resumeStepIndex > 0
          ? `刷新后续跑：从第 ${inferred.resumeStepIndex + 1} 个模块继续…`
          : '等待执行槽位…',
      },
    };
  });
  if (dirty) {
    saveWorkflowTasks(next);
    scheduleQueuePump(800);
  }
  return dirty;
}

export function ensureWorkflowRunnerStarted() {
  startWorkflowClientPresence();
  if (!runnerStarted) {
    runnerStarted = true;
    runningTaskIds.clear();
    hydrateWorkflowTasksFromServer().finally(() => {
      reconcileOrphanedWorkflowTasks();
      pumpWorkflowRunner();
    });
    return;
  }
  hydrateWorkflowTasksFromServer();
  pumpWorkflowRunner();
}

export function pumpWorkflowRunner() {
  reconcileOrphanedWorkflowTasks();
  const tasks = loadWorkflowTasks();
  const runningCount = tasks.filter((t) => t.status === 'running').length;
  if (runningCount >= WORKFLOW_MAX_CONCURRENT) return;

  const slots = WORKFLOW_MAX_CONCURRENT - runningCount;
  const queued = tasks.filter(
    (t) => t.status === 'queued'
      && (t.rows?.length ?? 0) > 0
      && !isTaskInRunner(t)
      && !isServerOwnedWorkflowTask(t)
      && !inferWorkflowResumeCursor(t).terminalStatus,
  ).slice(0, slots);
  if (queued.length === 0) return;
  queued.forEach((t) => executeWorkflowTask(t.id));
  if (tasks.some((t) => t.status === 'queued')) {
    scheduleQueuePump();
  }
}

async function fetchRemoteWorkflowTask(taskId) {
  if (!taskId) return null;
  try {
    const res = await workflowTaskAPI.getById(taskId);
    return res?.task || null;
  } catch {
    return null;
  }
}

async function executeWorkflowTask(taskId) {
  const resolvedStartId = resolveWorkflowTaskId(taskId);
  if (runningTaskIds.has(resolvedStartId) || runningTaskIds.has(taskId)) return;

  const resolvedId = resolveWorkflowTaskId(taskId);
  const task = getWorkflowTaskById(resolvedId);
  if (!task || task.status !== 'queued') return;

  // 必须在任何 await 之前占位，否则 pump 可能并发启动两条 pipeline（同毫秒 task_id 撞库）
  runningTaskIds.add(resolvedId);
  if (taskId !== resolvedId) runningTaskIds.add(taskId);

  const pendingCreate =
    serverCreatePromises.get(taskId) || serverCreatePromises.get(resolvedStartId);
  if (pendingCreate) {
    try {
      await pendingCreate;
    } catch {
      /* 本地仍可执行，服务端同步失败不阻塞 */
    }
  }

  const remote = await fetchRemoteWorkflowTask(resolvedId);
  if (isServerOwnedWorkflowTask(remote) && remote.status === 'running') {
    runningTaskIds.delete(resolvedId);
    if (taskId !== resolvedId) runningTaskIds.delete(taskId);
    const list = loadWorkflowTasks();
    const idx = findWorkflowTaskIndex(list, resolvedId);
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...remote, id: list[idx].id, status: 'running' };
      saveWorkflowTasks(list);
    }
    return;
  }

  const taskAfterSync = getWorkflowTaskById(resolvedId);
  if (!taskAfterSync) {
    runningTaskIds.delete(resolvedId);
    if (taskId !== resolvedId) runningTaskIds.delete(taskId);
    return;
  }
  if (isServerOwnedWorkflowTask(taskAfterSync) && taskAfterSync.status === 'running') {
    runningTaskIds.delete(resolvedId);
    if (taskId !== resolvedId) runningTaskIds.delete(taskId);
    return;
  }
  if (taskAfterSync.status !== 'queued') {
    runningTaskIds.delete(resolvedId);
    if (taskId !== resolvedId) runningTaskIds.delete(taskId);
    return;
  }
  const activeTask = taskAfterSync;

  const progressPatch = createDebouncedTaskPatch(resolvedId);
  const patchRunning = (patch, { debounceLive = false } = {}) => {
    if (debounceLive) {
      progressPatch.debounced(patch);
      return null;
    }
    progressPatch.flush();
    return progressPatch.now(patch);
  };
  const readRunning = () => getWorkflowTaskById(resolvedId);

  let slotAcquired = true;
  try {
    releaseSlotsForInactiveTasks();
    let slotRes = await workflowTaskAPI.acquireSlot(resolvedId);
    slotAcquired = slotRes?.acquired !== false;
    if (!slotAcquired) {
      releaseSlotsForInactiveTasks();
      await workflowTaskAPI.releaseSlot(resolvedId).catch(() => {});
      slotRes = await workflowTaskAPI.acquireSlot(resolvedId);
      slotAcquired = slotRes?.acquired !== false;
    }
    if (!slotAcquired) {
      const localRunning = loadWorkflowTasks().filter((t) => t.status === 'running').length;
      if (localRunning === 0) {
        slotAcquired = true;
      }
    }
  } catch {
    slotAcquired = true;
  }

  if (!slotAcquired) {
    runningTaskIds.delete(resolvedId);
    runningTaskIds.delete(taskId);
    patchRunning({
      status: 'queued',
      live: {
        ...readRunning()?.live,
        lastMessage: '等待执行槽位…',
      },
    });
    scheduleQueuePump();
    return;
  }

  const template = normalizeTemplate(activeTask.templateSnapshot);
  if (!template) {
    runningTaskIds.delete(resolvedId);
    runningTaskIds.delete(taskId);
    patchRunning({ status: 'failed', error: '无效的工作流模板快照' });
    return;
  }
  const pipelineSteps = resolveTemplatePipelineSteps(template);

  const retryRowIndices = Array.isArray(activeTask.retryRowIndices) && activeTask.retryRowIndices.length
    ? [...new Set(
      activeTask.retryRowIndices
        .map((i) => Number(i))
        .filter((i) => Number.isFinite(i) && i >= 0 && i < activeTask.rows.length),
    )].sort((a, b) => a - b)
    : null;
  const retryStepByRow =
    activeTask.retryStepByRow && typeof activeTask.retryStepByRow === 'object' ? activeTask.retryStepByRow : {};
  const inferredResume = retryRowIndices?.length ? null : inferWorkflowResumeCursor(activeTask);
  if (inferredResume?.terminalStatus) {
    runningTaskIds.delete(resolvedId);
    runningTaskIds.delete(taskId);
    patchRunning({
      status: inferredResume.terminalStatus,
      resumeFromRowIndex: inferredResume.resumeFromRowIndex,
      resumeStepIndex: inferredResume.resumeStepIndex,
      live: {
        ...(activeTask.live || {}),
        lastMessage: inferredResume.terminalStatus === 'terminated' ? '任务已终止' : '任务已完成',
      },
    });
    return;
  }
  const startRowIndex = retryRowIndices?.length
    ? retryRowIndices[0]
    : Math.max(0, Number(inferredResume?.resumeFromRowIndex) || Number(activeTask.resumeFromRowIndex) || 0);
  let resumeStepIndex = Math.max(
    0,
    Number(inferredResume?.resumeStepIndex) || 0,
    Number(activeTask.resumeStepIndex) || 0,
  );
  const priorResults = Array.isArray(activeTask.results) ? activeTask.results : [];
  if (!retryRowIndices?.length && resumeStepIndex <= 0) {
    const pending = activeTask.pendingReview;
    if (pending && Number.isFinite(Number(pending.stepIndex))) {
      resumeStepIndex = resolveWorkflowReviewContinueStepIndex(template, Number(pending.stepIndex));
    } else {
      const prior = priorResults[startRowIndex]?.result;
      if (prior?.pendingReview && Number.isFinite(Number(prior.reviewAtStepIndex))) {
        resumeStepIndex = resolveWorkflowReviewContinueStepIndex(template, Number(prior.reviewAtStepIndex));
      }
    }
  }
  const isReviewContinue = !retryRowIndices?.length && resumeStepIndex > 0;
  /** 续跑沿用原 runId，避免刷新后新 ID 把已完成步骤当成上一轮缓存丢掉 */
  const workflowRunId = activeTask.workflowRunId || Date.now();
  const initialResults = priorResults;
  const rowsToRun = retryRowIndices?.length
    ? retryRowIndices.map((i) => activeTask.rows[i])
    : activeTask.rows.slice(startRowIndex);

  const shouldAbort = () => taskAbortFlags.has(resolvedId) || taskAbortFlags.has(taskId);

  patchRunning({ templateSnapshot: template });

  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(`ipe-wf-listing-export-${resolvedId}`);
    } catch {
      /* ignore */
    }
  }

  const pendingStepFactory = () => ({
    status: 'pending',
    detail: '',
    inputImageUrl: '',
    outputImageUrl: '',
    previewLeftLabel: '',
    previewCenterLabel: '',
    previewRightLabel: '',
    modelReferenceImageUrl: '',
    modelReferencePresetId: '',
    modelReferenceModelType: '',
    modelReferenceGarmentType: '',
    modelReferenceUsedUploadedModel: false,
  });
  const initialResumeStep = resolveWorkflowRowResumeStepIndex({
    rowIndex: startRowIndex,
    startRowIndex,
    resumeStepIndex,
    retryRowIndices,
    retryStepByRow,
    priorResults,
    template,
  });

  patchRunning({
    workflowRunId,
    results: initialResults.length > 0 ? initialResults : null,
    summary:
      activeTask.summary && initialResults.length > 0
        ? { ...activeTask.summary, results: initialResults }
        : activeTask.summary,
    status: 'running',
    executor: 'client',
    serverRunner: false,
    clientExecutor: false,
    serverExecute: false,
    progress: {
      rowIndex: startRowIndex,
      rowTotal: activeTask.rows.length,
      stepIndex: initialResumeStep > 0 ? Math.max(0, initialResumeStep - 1) : 0,
      stepLabel: isReviewContinue ? (activeTask.pendingReview?.stepLabel || activeTask.progress?.stepLabel || '') : '',
    },
    live: {
      rowIndex: startRowIndex,
      stepStatuses: initialResumeStep > 0
        ? buildPreservedWorkflowStepDisplay({
          pipelineSteps,
          priorEntry: priorResults[startRowIndex],
          resumeStep: initialResumeStep,
          emptyFactory: pendingStepFactory,
        })
        : pipelineSteps.map(() => pendingStepFactory()),
      previewOriginal: priorResults[startRowIndex]?.result?.initialImageUrl || '',
      previewResult: priorResults[startRowIndex]?.result?.finalImageUrl || '',
      lastMessage: retryRowIndices?.length
        ? `从失败模块起重试整批任务…`
        : isReviewContinue
          ? `审核已通过，从第 ${startRowIndex + 1} 条第 ${resumeStepIndex + 1} 步继续…`
          : initialResumeStep > 0
            ? `从第 ${initialResumeStep + 1} 个模块继续…`
            : startRowIndex > 0
              ? `从第 ${startRowIndex + 1} / ${activeTask.rows.length} 条继续…`
              : '准备中…',
    },
  });

  try {
    const baseRuntimeOverrides =
      task.runtimeStepOverrides && Object.keys(task.runtimeStepOverrides).length
        ? task.runtimeStepOverrides
        : buildInitialRuntimeStepOverrides(template);
    const runtimeOverrides = hydrateModelStudioRuntimeOverrides(
      template,
      baseRuntimeOverrides,
      priorResults,
    );
    if (
      !task.runtimeStepOverrides
      || !Object.keys(task.runtimeStepOverrides).length
      || JSON.stringify(runtimeOverrides) !== JSON.stringify(task.runtimeStepOverrides)
    ) {
      patchRunning({ runtimeStepOverrides: runtimeOverrides });
    }
    const results = await runPipelineForRows(rowsToRun, template, {
      workflowRunId,
      workflowContext: {
        workflowRunId,
        workflowTaskId: resolvedId,
        templateName: template.name || '',
        operatorName: String(activeTask.operatorName || activeTask.operator || '').trim(),
        stampPreAssign: activeTask.stampPreAssign || null,
        allRows: activeTask.rows || [],
      },
      workflowRemark: activeTask.remark,
      rowIndexOffset: startRowIndex,
      rowIndices: retryRowIndices || undefined,
      totalRows: activeTask.rows.length,
      shouldAbort,
      getRowResumeOptions: (rowIndex) => {
        const stepIdx = resolveWorkflowRowResumeStepIndex({
          rowIndex,
          startRowIndex,
          resumeStepIndex,
          retryRowIndices,
          retryStepByRow,
          priorResults,
          template,
        });
        if (stepIdx <= 0) return null;
        const priorEntry = priorResults[rowIndex] || readRunning()?.results?.[rowIndex];
        const priorResult = priorEntry?.result;
        if (!priorResult) return { startStepIndex: stepIdx, resumeFromResult: null };
        const resumeResult = priorResult.pendingReview
          ? { ...priorResult, pendingReview: false, ok: true }
          : priorResult;
        return { startStepIndex: stepIdx, resumeFromResult: resumeResult };
      },
      getRowWithOverrides: (row) => {
        const cur = readRunning();
        const ov = cur?.runtimeStepOverrides || runtimeOverrides;
        const [merged] = applyRuntimeStepOverridesToRows([row], ov);
        return merged;
      },
      onInfringementRiskPrompt: async (ctx) => {
        const rowIndex = ctx.rowIndex ?? readRunning()?.progress?.rowIndex ?? 0;
        const resolvedId = resolveWorkflowTaskId(taskId);
        patchRunning({
          live: {
            ...readRunning()?.live,
            pendingPrompt: {
              type: 'infringement',
              rowIndex,
              rowId: ctx.row?.id,
              advice: ctx.advice,
              counts: ctx.counts,
              batchNo: ctx.batchNo,
              topRisk: ctx.topRisk,
            },
          },
        });
        const decision = await waitForInfringementDecision(resolvedId, rowIndex);
        patchRunning({
          live: {
            ...readRunning()?.live,
            pendingPrompt: null,
          },
        });
        return decision;
      },
      onGoodsStampAssignPrompt: async (ctx) => {
        const rowIndex = ctx.rowIndex ?? readRunning()?.progress?.rowIndex ?? 0;
        const resolvedId = resolveWorkflowTaskId(taskId);
        patchRunning({
          live: {
            ...readRunning()?.live,
            pendingPrompt: {
              type: 'goodsStampAssign',
              rowIndex,
              rowId: ctx.row?.id,
              navId: ctx.navId,
              stepLabel: ctx.stepLabel,
              templateIds: ctx.templateIds || [],
              defaultStamp: ctx.defaultStamp || null,
              stampSourceLabel: ctx.stampSourceLabel || '',
            },
          },
        });
        const assignment = await waitForGoodsStampAssignment(resolvedId, rowIndex);
        patchRunning({
          live: {
            ...readRunning()?.live,
            pendingPrompt: null,
          },
        });
        return assignment;
      },
      onRowStart: ({ rowIndex, total }) => {
        if (shouldAbort()) return;
        const cur = readRunning();
        const mergedResults = Array.isArray(cur?.results) ? [...cur.results] : [];
        const row = activeTask.rows[rowIndex];
        const pendingStep = pendingStepFactory;
        const resumeStep = resolveWorkflowRowResumeStepIndex({
          rowIndex,
          startRowIndex,
          resumeStepIndex,
          retryRowIndices,
          retryStepByRow,
          priorResults,
          template,
        });
        const priorEntry = priorResults[rowIndex] || mergedResults[rowIndex];
        const priorResult = priorEntry?.result;
        const preservedSteps = Array.isArray(priorResult?.steps) && resumeStep > 0
          ? priorResult.steps.slice(0, resumeStep)
          : (Array.isArray(priorResult?.steps) ? priorResult.steps : []);
        const preservedStepDisplay = resumeStep > 0
          ? buildPreservedWorkflowStepDisplay({
            pipelineSteps,
            priorEntry,
            resumeStep,
            emptyFactory: pendingStep,
          })
          : (Array.isArray(priorEntry?.stepDisplay) && priorEntry.stepDisplay.length
            ? priorEntry.stepDisplay
            : pipelineSteps.map(() => pendingStep()));
        mergedResults[rowIndex] = {
          row: row || priorEntry?.row,
          result: {
            ...(priorResult || {}),
            inProgress: true,
            ok: false,
            steps: preservedSteps,
            skipped: false,
            workflowRunId: cur?.workflowRunId ?? workflowRunId,
            finalImageUrl: priorResult?.finalImageUrl,
            finalSpaceId: priorResult?.finalSpaceId,
            initialImageUrl: priorResult?.initialImageUrl,
          },
          stepDisplay: preservedStepDisplay,
        };
        patchRunning({
          progress: {
            rowIndex,
            rowTotal: total,
            stepIndex: resumeStep > 0 ? resumeStep - 1 : 0,
            stepLabel: resumeStep > 0 ? (activeTask.pendingReview?.stepLabel || activeTask.progress?.stepLabel || '') : '',
          },
          results: mergedResults,
          live: {
            ...cur?.live,
            rowIndex,
            stepStatuses: preservedStepDisplay,
            previewOriginal: priorResult?.initialImageUrl || cur?.live?.previewOriginal || '',
            previewResult: priorResult?.finalImageUrl || cur?.live?.previewResult || '',
            lastMessage: resumeStep > 0
              ? (retryRowIndices?.length
                ? `重试 ${total} 组印花，保留已完成步骤…`
                : `从第 ${resumeStep + 1} 个模块继续，已完成步骤保留…`)
              : `按模块整批执行 · 已接入 ${rowIndex + 1}/${total} 组`,
          },
        });
      },
      onStep: (ev) => {
        if (shouldAbort()) return;
        const cur = readRunning();
        const live = cur?.live || {};
        const stepStatuses = [...(live.stepStatuses || pipelineSteps.map(() => ({})))];
        const idx = ev.index;
        const mapped = mapWorkflowStepEventStatus(ev.status);
        if (ev.status === 'running') {
          stepStatuses[idx] = {
            status: 'running',
            detail: '',
            inputImageUrl: ev.inputImageUrl || '',
            outputImageUrl: '',
            previewLeftLabel: ev.previewLeftLabel || '',
            previewCenterLabel: ev.previewCenterLabel || '',
            previewRightLabel: ev.previewRightLabel || '',
            modelReferenceImageUrl: ev.modelReferenceImageUrl || '',
            modelReferencePresetId: ev.modelReferencePresetId || '',
            modelReferenceModelType: ev.modelReferenceModelType || '',
            modelReferenceGarmentType: ev.modelReferenceGarmentType || '',
            modelReferenceUsedUploadedModel: ev.modelReferenceUsedUploadedModel === true,
            outputVideoUrl: '',
            outputVideoUrls: [],
          };
        } else if (mapped === 'done' || mapped === 'skipped' || mapped === 'stopped' || mapped === 'risk') {
          stepStatuses[idx] = {
            status: mapped,
            detail: ev.detail || '',
            inputImageUrl: ev.inputImageUrl || '',
            outputImageUrl: ev.outputImageUrl || '',
            previewLeftLabel: ev.previewLeftLabel || '',
            previewCenterLabel: ev.previewCenterLabel || '',
            previewRightLabel: ev.previewRightLabel || '',
            modelReferenceImageUrl: ev.modelReferenceImageUrl || '',
            modelReferencePresetId: ev.modelReferencePresetId || '',
            modelReferenceModelType: ev.modelReferenceModelType || '',
            modelReferenceGarmentType: ev.modelReferenceGarmentType || '',
            modelReferenceUsedUploadedModel: ev.modelReferenceUsedUploadedModel === true,
            outputVideoUrl: ev.outputVideoUrl || '',
            outputVideoUrls: Array.isArray(ev.outputVideoUrls) ? ev.outputVideoUrls : [],
            ...pickWorkflowStepLinkFields(ev),
          };
        } else if (mapped === 'failed') {
          stepStatuses[idx] = {
            status: 'failed',
            detail: ev.error || ev.detail || '',
            inputImageUrl: ev.inputImageUrl || '',
            outputImageUrl: ev.outputImageUrl || '',
            previewLeftLabel: ev.previewLeftLabel || '',
            previewCenterLabel: ev.previewCenterLabel || '',
            previewRightLabel: ev.previewRightLabel || '',
            modelReferenceImageUrl: ev.modelReferenceImageUrl || '',
            modelReferencePresetId: ev.modelReferencePresetId || '',
            modelReferenceModelType: ev.modelReferenceModelType || '',
            modelReferenceGarmentType: ev.modelReferenceGarmentType || '',
            modelReferenceUsedUploadedModel: ev.modelReferenceUsedUploadedModel === true,
            outputVideoUrl: '',
            outputVideoUrls: [],
          };
        }
        const nextResults = applyWorkflowStepEventToResults(
          cur?.results,
          pipelineSteps,
          ev,
          pendingStepFactory,
        );
        const stageLabel = ev.label || `第 ${(Number(ev.stageIndex) || idx) + 1} 个`;
        patchRunning({
          progress: {
            rowIndex: ev.rowIndex,
            rowTotal: ev.total,
            stepIndex: idx,
            stepLabel: ev.label || '',
            ...(Number.isFinite(Number(ev.stageIndex))
              ? {
                stageIndex: Number(ev.stageIndex),
                stageTotal: Number(ev.stageTotal) || pipelineSteps.length,
              }
              : {}),
          },
          results: nextResults,
          live: {
            ...live,
            rowIndex: ev.rowIndex,
            stepStatuses,
            previewOriginal: ev.inputImageUrl || live.previewOriginal,
            previewResult: ev.outputImageUrl || live.previewResult,
            lastMessage: Number.isFinite(Number(ev.stageIndex))
              ? (mapped === 'running'
                ? `正在处理模块「${stageLabel}」· 全部 ${ev.total} 组`
                : `模块「${stageLabel}」${mapped === 'done' ? '已完成' : '已更新'}`)
              : `正在处理第 ${ev.rowIndex + 1} / ${ev.total} 条… · ${ev.label || ''}`,
          },
        });
      },
      onRowProgress: ({ rowIndex, result, row }) => {
        if (shouldAbort()) return;
        const cur = readRunning();
        const mergedResults = Array.isArray(cur?.results) ? [...cur.results] : [];
        const runId = cur?.workflowRunId ?? workflowRunId;
        const taggedResult =
          runId != null ? { ...result, workflowRunId: runId } : result;
        const stepDisplay = buildWorkflowRowStepDisplay(pipelineSteps, taggedResult, () => ({
          status: 'pending',
          detail: '',
          inputImageUrl: '',
          outputImageUrl: '',
          previewLeftLabel: '',
          previewCenterLabel: '',
          previewRightLabel: '',
          modelReferenceImageUrl: '',
          modelReferencePresetId: '',
          modelReferenceModelType: '',
          modelReferenceGarmentType: '',
          modelReferenceUsedUploadedModel: false,
        }));
        mergedResults[rowIndex] = { row, result: taggedResult, stepDisplay };
        patchRunning({
          results: mergedResults,
          live: {
            ...cur?.live,
            rowIndex,
            previewResult: result.finalImageUrl || row?.primaryImageUrl || cur?.live?.previewResult,
          },
        });
      },
      onRowDone: ({ rowIndex, total, result, row }) => {
        if (shouldAbort()) return;
        const cur = readRunning();
        const mergedResults = Array.isArray(cur?.results) ? [...cur.results] : [];
        const runId = cur?.workflowRunId ?? workflowRunId;
        const taggedResult =
          runId != null ? { ...result, workflowRunId: runId } : result;
        const stepDisplay = buildWorkflowRowStepDisplay(pipelineSteps, taggedResult, () => ({
          status: 'pending',
          detail: '',
          inputImageUrl: '',
          outputImageUrl: '',
          previewLeftLabel: '',
          previewCenterLabel: '',
          previewRightLabel: '',
          modelReferenceImageUrl: '',
          modelReferencePresetId: '',
          modelReferenceModelType: '',
          modelReferenceGarmentType: '',
          modelReferenceUsedUploadedModel: false,
        }));
        mergedResults[rowIndex] = { row, result: taggedResult, stepDisplay };
        patchRunning({
          progress: {
            rowIndex,
            rowTotal: total,
            stepIndex: pipelineSteps.length - 1,
            stepLabel: '',
          },
          results: mergedResults,
          resumeFromRowIndex: result.pendingReview ? rowIndex : rowIndex + 1,
          live: {
            ...cur?.live,
            rowIndex,
            previewResult: result.finalImageUrl || row?.primaryImageUrl || cur?.live?.previewResult,
            lastMessage: result.pendingReview
              ? (result.reviewStepLabel
                ? `步骤「${result.reviewStepLabel}」已完成，等待人工审核`
                : '等待人工审核')
              : result.ok
                ? `第 ${rowIndex + 1} / ${total} 条已完成`
                : result.error || '失败',
          },
        });
      },
    });

    progressPatch.flush();

    const allResults = mergeWorkflowResultsAtEnd(
        priorResults,
        readRunning()?.results,
        results,
        startRowIndex,
        workflowRunId,
        retryRowIndices,
    );
    const totalRows = activeTask.rows.length;
    let successCount = 0;
    let failedCount = 0;
    let partialCount = 0;
    allResults.forEach((entry) => {
      const outcome = classifyWorkflowRowOutcome(entry?.result, pipelineSteps);
      if (outcome === 'success') successCount += 1;
      else if (outcome === 'partial') partialCount += 1;
      else if (outcome === 'failed') failedCount += 1;
    });
    const okCount = successCount;
    const hadSkipped = allResults.some(
      (x) =>
        x.result?.skipped ||
        (x.result?.steps || []).some((s) => s.status === 'skipped'),
    );
    const remarkSuffix = activeTask.remark ? `（备注：${activeTask.remark}）` : '';
    const skipHint = hadSkipped ? '；部分步骤为「跳过」' : '';
    const finalStatus = successCount >= totalRows
      ? 'done'
      : (partialCount > 0 || (successCount > 0 && failedCount > 0))
        ? 'partial'
        : failedCount > 0
          ? 'failed'
          : 'failed';
    const lastMessage = successCount >= totalRows
      ? `全部 ${totalRows} 条已按流程跑完${remarkSuffix}${skipHint}`
      : partialCount > 0 && successCount === 0 && failedCount === 0
        ? `${partialCount} 条部分失败${remarkSuffix}${skipHint}`
        : successCount > 0
          ? `完成 ${successCount}/${totalRows} 条，部分失败${remarkSuffix}`
          : `全部 ${totalRows} 条执行失败${remarkSuffix}`;

    persistSourcingWorkflowRuns(template, allResults);

    const finalTask = readRunning();
    let exportSummary = null;
    try {
      exportSummary = await finalizeWorkflowListingExport(allResults, finalTask, template, resolvedId);
    } catch (e) {
      exportSummary = { exported: false, error: e.message || String(e) };
    }

    const exportOk = exportSummary?.exported;
    const exportHint = exportOk
      ? (() => {
          const n = exportSummary.rowCount ?? 0;
          const excelN = exportSummary.bundle?.excelCount ?? exportSummary.templateCount ?? 1;
          const imgN = exportSummary.bundle?.imageCount ?? 0;
          const zip = exportSummary.bundle?.zipName;
          const dlOk = exportSummary.bundle?.downloadTriggered !== false;
          return dlOk
            ? `流程全部完成，已合并导出 ${n} 条（${excelN} 份表格 + ${imgN} 张图${zip ? `，打包为 ${zip}` : ''}）。若未弹出保存，请点击「下载 ZIP」`
            : `流程全部完成，已生成 ${zip || '上架包'}（${n} 条），但浏览器未自动保存。请点击「下载 ZIP」`;
        })()
      : exportSummary?.error
        ? `流程完成，但表格导出失败：${exportSummary.error}`
        : null;

    const pendingReviewRowIndex = allResults.findIndex((entry) => entry?.result?.pendingReview);
    if (pendingReviewRowIndex >= 0 && !shouldAbort()) {
      const reviewResult = allResults[pendingReviewRowIndex]?.result || {};
      const reviewStepIndex = Math.max(0, Number(reviewResult.reviewAtStepIndex) || 0);
      const continueStepIndex = resolveWorkflowReviewContinueStepIndex(template, reviewStepIndex);
      const reviewLabel = reviewResult.reviewStepLabel || '';
      const pendingReviewCount = allResults.filter((entry) => entry?.result?.pendingReview).length;
      patchRunning({
        status: 'pendingReview',
        results: allResults,
        resumeFromRowIndex: pendingReviewRowIndex,
        resumeStepIndex: continueStepIndex,
        retryRowIndices: null,
        retryStepByRow: null,
        pendingReview: {
          rowIndex: pendingReviewRowIndex,
          stepIndex: reviewStepIndex,
          stepLabel: reviewLabel,
          navId: reviewResult.reviewNavId || '',
        },
        summary: { okCount, total: totalRows, results: allResults, exportSummary },
        live: {
          ...readRunning()?.live,
          lastMessage: reviewLabel
            ? `步骤「${reviewLabel}」已完成，等待人工审核${pendingReviewCount > 1 ? `（另有 ${pendingReviewCount - 1} 条待审）` : ''}`
            : '等待人工审核',
          exportComplete: exportOk,
        },
        error: null,
      });
    } else if (shouldAbort()) {
      taskAbortFlags.delete(resolvedId);
      taskAbortFlags.delete(taskId);
      const cur = readRunning();
      const resumeFromRowIndex = Math.max(
        0,
        Number.isFinite(Number(cur?.resumeFromRowIndex)) ? Number(cur.resumeFromRowIndex) : startRowIndex,
      );
      patchRunning({
        status: 'terminated',
        terminatedByUser: true,
        results: allResults,
        resumeFromRowIndex,
        resumeStepIndex: 0,
        retryRowIndices: null,
        retryStepByRow: null,
        pendingReview: null,
        summary: { okCount, total: totalRows, results: allResults, exportSummary },
        live: {
          ...cur?.live,
          lastMessage: '任务已终止',
          exportComplete: exportOk,
          pendingPrompt: null,
        },
        error: null,
      });
    } else {
      patchRunning({
        status: finalStatus,
        results: allResults,
        resumeFromRowIndex: totalRows,
        resumeStepIndex: 0,
        retryRowIndices: null,
        retryStepByRow: null,
        pendingReview: null,
        summary: { okCount, total: totalRows, results: allResults, exportSummary },
        live: {
          ...readRunning()?.live,
          lastMessage: exportHint || lastMessage,
          exportComplete: exportOk,
        },
        error: okCount === totalRows ? null : `${totalRows - okCount} 条失败`,
      });
    }
  } catch (e) {
    if (e?.name === 'WorkflowManualReviewRequiredError') {
      progressPatch.flush();
      taskAbortFlags.delete(resolvedId);
      taskAbortFlags.delete(taskId);
      const cur = readRunning();
      const reviewRowIndex = Number.isFinite(Number(e.rowIndex))
        ? Number(e.rowIndex)
        : Number(cur?.progress?.rowIndex) || startRowIndex;
      const reviewStepIndex = Math.max(0, Number(e.result?.reviewAtStepIndex) || 0);
      const continueStepIndex = resolveWorkflowReviewContinueStepIndex(template, reviewStepIndex);
      const reviewLabel = e.result?.reviewStepLabel || cur?.progress?.stepLabel || '';
      patchRunning({
        status: 'pendingReview',
        resumeFromRowIndex: reviewRowIndex,
        resumeStepIndex: continueStepIndex,
        pendingReview: {
          rowIndex: reviewRowIndex,
          stepIndex: reviewStepIndex,
          stepLabel: reviewLabel,
          navId: e.result?.reviewNavId || '',
        },
        live: {
          ...cur?.live,
          lastMessage: reviewLabel
            ? `步骤「${reviewLabel}」已完成，等待人工审核`
            : '等待人工审核',
        },
        error: null,
      });
    } else if (e?.batchTerminated || e?.name === 'WorkflowBatchTerminatedError') {
      // 步骤失败且模板设置为「终止整批」：终止后续所有条目
      taskAbortFlags.delete(resolvedId);
      taskAbortFlags.delete(taskId);
      const cur = readRunning();
      const partialResults = Array.isArray(cur?.results) ? cur.results : [];
      let terminateExportSummary = null;
      try {
        terminateExportSummary = await finalizeWorkflowListingExport(
          partialResults,
          cur,
          template,
          resolvedId,
        );
      } catch (exportErr) {
        terminateExportSummary = { exported: false, error: exportErr.message || String(exportErr) };
      }
      const okCount = partialResults.filter((x) => x.result?.ok).length;
      const failedRowIndex = Math.max(
        0,
        Number.isFinite(Number(cur?.progress?.rowIndex))
          ? Number(cur.progress.rowIndex)
          : startRowIndex,
      );
      patchRunning({
        status: 'terminated',
        terminatedByStepFailure: true,
        resumeFromRowIndex: failedRowIndex,
        resumeStepIndex: 0,
        pendingReview: null,
        results: partialResults,
        summary: {
          okCount,
          total: activeTask.rows.length,
          results: partialResults,
          exportSummary: terminateExportSummary || undefined,
        },
        live: {
          ...cur?.live,
          lastMessage: e?.stepLabel
            ? `步骤「${e.stepLabel}」失败，已按模板设置终止整批任务`
            : '步骤失败，已按模板设置终止整批任务',
          exportComplete: terminateExportSummary?.exported || false,
          pendingPrompt: null,
        },
        error: null,
      });
    } else if (e?.name === 'WorkflowTaskAbortedError' || e?.message === '任务已终止' || shouldAbort()) {
      taskAbortFlags.delete(resolvedId);
      taskAbortFlags.delete(taskId);
      const cur = readRunning();
      const resumeFromRowIndex = Math.max(
        0,
        Number.isFinite(Number(cur?.resumeFromRowIndex)) ? Number(cur.resumeFromRowIndex) : startRowIndex,
      );
      const partialResults = Array.isArray(cur?.results) ? cur.results : [];
      let terminateExportSummary = null;
      try {
        terminateExportSummary = await finalizeWorkflowListingExport(
          partialResults,
          cur,
          template,
          resolvedId,
        );
      } catch (exportErr) {
        terminateExportSummary = { exported: false, error: exportErr.message || String(exportErr) };
      }
      const okCount = partialResults.filter((x) => x.result?.ok).length;
      patchRunning({
        status: 'terminated',
        terminatedByUser: true,
        resumeFromRowIndex,
        resumeStepIndex: 0,
        pendingReview: null,
        results: partialResults,
        summary: {
          okCount,
          total: activeTask.rows.length,
          results: partialResults,
          exportSummary: terminateExportSummary || undefined,
        },
        live: {
          ...cur?.live,
          lastMessage: '任务已终止',
          exportComplete: terminateExportSummary?.exported || false,
          pendingPrompt: null,
        },
        error: null,
      });
    } else {
      patchRunning({
        status: 'failed',
        error: e.message || String(e),
        live: {
          ...readRunning()?.live,
          lastMessage: e.message || String(e),
        },
      });
    }
  } finally {
    const after = readRunning();
    if (after && isTerminalWorkflowStatus(after.status)) {
      // 终态：丢掉还没落地的「正在处理」live 补丁，再经 patchTask 走展示修正
      progressPatch.discard();
      patchTask(resolvedId, { status: after.status });
    } else {
      progressPatch.flush();
    }
    workflowTaskAPI.releaseSlot(resolvedId).catch(() => {});
    runningTaskIds.delete(resolvedId);
    runningTaskIds.delete(taskId);
    pumpWorkflowRunner();
    // 跑完后若已无排队/进行中，停掉 client-presence；有后续任务则保持/拉起
    startWorkflowClientPresence();
    if (runningTaskIds.size === 0) {
      window.setTimeout(() => hydrateWorkflowTasksFromServer(), 1200);
    }
  }
}

export function updateWorkflowTaskRemark(taskId, payload) {
  if (typeof payload === 'string') {
    return patchTask(taskId, { remark: String(payload || '').trim() });
  }
  return patchTask(taskId, {
    remark: String(payload?.remark ?? '').trim(),
    remarkFlag: String(payload?.remarkFlag ?? '').trim(),
  });
}

export function patchWorkflowRuntimeStepOverrides(taskId, navId, paramsPatch) {
  const t = getWorkflowTaskById(taskId);
  if (!t || !navId) return null;
  const runtimeStepOverrides = { ...(t.runtimeStepOverrides || {}) };
  runtimeStepOverrides[navId] = {
    ...(runtimeStepOverrides[navId] || {}),
    ...(paramsPatch && typeof paramsPatch === 'object' ? paramsPatch : {}),
  };
  return patchTask(taskId, { runtimeStepOverrides });
}

export async function cancelWorkflowTask(taskId) {
  const t = getWorkflowTaskById(taskId);
  if (!t || t.status !== 'queued') return false;
  await removeWorkflowTask(taskId);
  return true;
}

/** 执行中任务：标记终止并停止流水线 */
export function terminateWorkflowTask(taskId) {
  const t = getWorkflowTaskById(taskId);
  if (!t || t.status !== 'running') return false;

  const resolvedId = resolveWorkflowTaskId(taskId);
  const abortIds = [...new Set([resolvedId, taskId, t.legacyLocalId].filter(Boolean))];
  abortIds.forEach((id) => taskAbortFlags.add(id));
  abortIds.forEach((id) => cancelInfringementWaiters(id));
  abortIds.forEach((id) => cancelGoodsStampWaiters(id));
  abortIds.forEach((id) => cancelPreAssignWaiters(id));

  const partialResults = Array.isArray(t.results) ? t.results : [];
  const okCount = partialResults.filter((x) => x?.result?.ok).length;

  patchTask(taskId, {
    status: 'terminated',
    terminatedByUser: true,
    pendingReview: null,
    summary: {
      ...(t.summary || {}),
      okCount,
      total: t.rows?.length || 0,
      results: partialResults.length ? partialResults : t.summary?.results,
    },
    live: {
      ...(t.live || {}),
      lastMessage: '任务已终止',
      pendingPrompt: null,
    },
  });
  return true;
}

function findFirstFailedWorkflowStepIndex(result, pipelineSteps) {
  return findFirstRetryWorkflowStepIndex(result, pipelineSteps);
}

function queuePartialWorkflowRetry(taskId, rowIndices, stepByRow, message) {
  const t = getWorkflowTaskById(taskId);
  if (!t) return false;
  if (t.status === 'running' || t.status === 'queued') return false;

  const template = normalizeTemplate(t.templateSnapshot);
  const pipelineSteps = template ? resolveTemplatePipelineSteps(template) : [];

  const sorted = [...new Set(
    rowIndices
      .map((i) => Number(i))
      .filter((i) => Number.isFinite(i) && i >= 0 && i < (t.rows?.length || 0)),
  )].sort((a, b) => a - b);
  if (sorted.length === 0) return false;

  const resolvedId = resolveWorkflowTaskId(taskId);
  taskAbortFlags.delete(resolvedId);
  taskAbortFlags.delete(taskId);

  const entries = getWorkflowTaskResultEntries(t);
  const firstRow = sorted[0];
  const firstResumeStep = Number(stepByRow[firstRow] ?? stepByRow[String(firstRow)] ?? 0) || 0;
  const preservedStepStatuses = firstResumeStep > 0
    ? buildPreservedWorkflowStepDisplay({
      pipelineSteps,
      priorEntry: entries[firstRow],
      resumeStep: firstResumeStep,
    })
    : [];

  patchTask(taskId, {
    status: 'queued',
    resumeFromRowIndex: sorted[0],
    resumeStepIndex: 0,
    retryRowIndices: sorted,
    retryStepByRow: stepByRow || {},
    pendingReview: null,
    error: null,
    progress: {
      rowIndex: sorted[0],
      rowTotal: t.rows?.length || 0,
      stepIndex: firstResumeStep > 0 ? Math.max(0, firstResumeStep - 1) : 0,
      stepLabel: '',
    },
    live: {
      ...(t.live || {}),
      rowIndex: sorted[0],
      stepStatuses: preservedStepStatuses,
      lastMessage: message || `排队重试 ${sorted.length} 组印花…`,
      pendingPrompt: null,
    },
  });
  ensureWorkflowRunnerStarted();
  return true;
}

/** 从指定模块起重试整批任务：该模块整批重跑，成功后继续后续模块 */
export function retryWorkflowTaskFromStep(taskId, stepIndex) {
  const t = getWorkflowTaskById(taskId);
  if (!t) return false;
  const template = normalizeTemplate(t.templateSnapshot);
  const pipelineSteps = template ? resolveTemplatePipelineSteps(template) : [];
  const total = t.rows?.length || 0;
  if (total <= 0) return false;

  const stepIdx = Number.isFinite(Number(stepIndex))
    ? Math.max(0, Math.min(Number(stepIndex), Math.max(0, pipelineSteps.length - 1)))
    : 0;
  const videoMaintenance = assertVideoToolsAvailable();
  if (!videoMaintenance.ok && workflowPipelineUsesVideoTools(pipelineSteps.slice(stepIdx))) {
    throw new Error(videoMaintenance.message);
  }
  const stepLabel = pipelineSteps[stepIdx]?.label || `模块 ${stepIdx + 1}`;
  const rows = Array.from({ length: total }, (_, i) => i);
  const stepByRow = {};
  rows.forEach((i) => {
    stepByRow[i] = stepIdx;
  });

  return queuePartialWorkflowRetry(
    taskId,
    rows,
    stepByRow,
    `排队从「${stepLabel}」重试该模块，成功后继续后续步骤…`,
  );
}

export function retryWorkflowTaskRowStep(taskId, rowIndex, stepIndex) {
  return retryWorkflowTaskFromStep(taskId, stepIndex);
}

/** 仅重试失败条目（可选限定 rowIndices），保留已成功条目结果 */
export function retryWorkflowTaskFailedRows(taskId, { rowIndices } = {}) {
  const t = getWorkflowTaskById(taskId);
  if (!t) return false;
  const template = normalizeTemplate(t.templateSnapshot);
  const pipelineSteps = template ? resolveTemplatePipelineSteps(template) : [];
  const filterSet = Array.isArray(rowIndices) && rowIndices.length
    ? new Set(rowIndices.map((i) => Number(i)).filter((i) => Number.isFinite(i)))
    : null;

  const entries = getWorkflowTaskResultEntries(t);
  const retryRows = [];
  const stepByRow = {};
  entries.forEach((entry, index) => {
    if (filterSet && !filterSet.has(index)) return;
    const result = entry?.result;
    const outcome = classifyWorkflowRowOutcome(result, pipelineSteps);
    if (!result || result.inProgress || outcome === 'pending' || outcome === 'review' || outcome === 'success') return;
    retryRows.push(index);
    stepByRow[index] = findFirstFailedWorkflowStepIndex(result, pipelineSteps);
  });

  if (retryRows.length === 0) return false;

  return queuePartialWorkflowRetry(
    taskId,
    retryRows,
    stepByRow,
    retryRows.length === 1
      ? '排队重试 1 组失败印花…'
      : `排队重试 ${retryRows.length} 组失败印花…`,
  );
}

/** 人工审核通过后继续执行 */
export function approveWorkflowTaskReview(taskId) {
  const t = getWorkflowTaskById(taskId);
  if (!t || t.status !== 'pendingReview') return false;

  const { rowIndex, stepIndex } = resolveWorkflowReviewResumeIndices(t);
  if (rowIndex >= (t.rows?.length || 0)) return false;
  if (stepIndex <= 0) return false;

  const resolvedId = resolveWorkflowTaskId(taskId);
  taskAbortFlags.delete(resolvedId);
  taskAbortFlags.delete(taskId);

  patchTask(taskId, {
    status: 'queued',
    pendingReview: null,
    resumeFromRowIndex: rowIndex,
    resumeStepIndex: stepIndex,
    retryRowIndices: null,
    retryStepByRow: null,
    error: null,
    progress: {
      rowIndex,
      rowTotal: t.rows?.length || 0,
      stepIndex: Math.max(0, stepIndex - 1),
      stepLabel: t.pendingReview?.stepLabel || '',
    },
    live: {
      ...t.live,
      lastMessage: `人工审核已通过，将从第 ${rowIndex + 1} 条第 ${stepIndex + 1} 步继续…`,
    },
  });
  ensureWorkflowRunnerStarted();
  return true;
}

/** 已完成/部分失败任务：清空结果并从第 1 条重新排队（刷新后重跑必须用此或新建任务） */
export function restartWorkflowTask(taskId) {
  const t = getWorkflowTaskById(taskId);
  if (!t) return false;
  if (t.status === 'running' || t.status === 'queued') return false;

  const videoMaintenance = assertVideoToolsAvailable();
  if (!videoMaintenance.ok) {
    const pipelineSteps = resolveTemplatePipelineSteps(t.templateSnapshot || t.template);
    if (workflowPipelineUsesVideoTools(pipelineSteps)) {
      throw new Error(videoMaintenance.message);
    }
  }

  const resolvedId = resolveWorkflowTaskId(taskId);
  taskAbortFlags.delete(resolvedId);
  taskAbortFlags.delete(taskId);

  patchTask(taskId, {
    status: 'queued',
    resumeFromRowIndex: 0,
    resumeStepIndex: 0,
    retryRowIndices: null,
    retryStepByRow: null,
    pendingReview: null,
    results: null,
    summary: null,
    error: null,
    workflowRunId: null,
    progress: {
      rowIndex: 0,
      rowTotal: t.rows?.length || 0,
      stepIndex: 0,
      stepLabel: '',
    },
    live: {
      ...(t.live || {}),
      rowIndex: 0,
      stepStatuses: [],
      lastMessage: '排队等待从头重新执行…',
      exportComplete: false,
      pendingPrompt: null,
    },
  });
  ensureWorkflowRunnerStarted();
  return true;
}

export async function removeWorkflowTask(taskId) {
  const list = loadWorkflowTasks();
  const t = list.find((task) => task.id === taskId || task.legacyLocalId === taskId)
    || getWorkflowTaskById(taskId);
  const resolvedId = resolveWorkflowTaskIdFromList(taskId, list);

  const idsToTry = [...new Set([resolvedId, taskId, t?.id, t?.legacyLocalId].filter(Boolean))];
  let lastError = null;
  for (const id of idsToTry) {
    try {
      await workflowTaskAPI.delete(id);
      lastError = null;
      break;
    } catch (e) {
      if (e?.status === 404) continue;
      // 本地缓存任务可能不属于当前账号（换号/旧数据）；仍允许从列表移除
      if (e?.status === 403) continue;
      lastError = e;
      break;
    }
  }
  if (lastError) throw lastError;

  if (t) {
    markWorkflowTaskDeletedKeys(t);
  } else {
    markWorkflowTaskDeleted(resolvedId);
    if (resolvedId !== taskId) markWorkflowTaskDeleted(taskId);
  }

  const removedIds = new Set(idsToTry.map(String));
  const next = filterDeletedWorkflowTasks(
    list.filter((task) => !removedIds.has(String(task.id)) && !removedIds.has(String(task.legacyLocalId || ''))),
  );
  saveWorkflowTasks(next);
}

export function countActiveWorkflowTasks() {
  return loadWorkflowTasks().filter((t) => t.status === 'queued' || t.status === 'running').length;
}

const WORKFLOW_STATUS_I18N = {
  loading: 'workflowHub.statusLoading',
  queued: 'workflowHub.statusQueued',
  running: 'workflowHub.statusRunning',
  done: 'workflowHub.statusDone',
  success: 'workflowHub.statusDone',
  partial: 'workflowHub.statusPartial',
  terminated: 'workflowHub.statusTerminated',
  failed: 'workflowHub.statusFailed',
  pendingReview: 'workflowHub.statusReview',
};

export function workflowTaskStatusLabel(status) {
  const key = WORKFLOW_STATUS_I18N[status];
  if (!key) return status || '—';
  return i18n.t(key, { ns: 'workbench' });
}
